import { useQuery } from '@tanstack/react-query';
import { ChevronDownIcon, ChevronsDownUpIcon, ChevronsUpDownIcon, ChevronUpIcon, CircleXIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { JsonViewer } from '~/modules/common/json-viewer';
import { getPathToNthMatch } from '~/modules/common/json-viewer/utils';
import { SearchSpinner } from '~/modules/common/search-spinner';
import { Spinner } from '~/modules/common/spinner';
import { JsonActions } from '~/modules/docs/json-actions';
import { openApiSpecQueryOptions, openApiUrl } from '~/modules/docs/query';
import { Button } from '~/modules/ui/button';
import { InputGroup, InputGroupAddon, InputGroupInput } from '~/modules/ui/input-group';
import { cn } from '~/utils/cn';

/** Counts matches recursively over both keys and primitive values. */
function countSearchMatches(value: unknown, searchText: string): number {
  if (!searchText) return 0;
  const lowerSearch = searchText.toLowerCase();
  let count = 0;

  if (value === null || value === undefined) return 0;
  if (typeof value === 'string') {
    if (value.toLowerCase().includes(lowerSearch)) count++;
  } else if (typeof value === 'number') {
    if (String(value).includes(searchText)) count++;
  } else if (typeof value === 'boolean') {
    if (String(value).toLowerCase().includes(lowerSearch)) count++;
  } else if (Array.isArray(value)) {
    for (const item of value) {
      count += countSearchMatches(item, searchText);
    }
  } else if (typeof value === 'object') {
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      if (key.toLowerCase().includes(lowerSearch)) count++;
      count += countSearchMatches(val, searchText);
    }
  }

  return count;
}

/** Full OpenAPI spec JSON, rendered by json-viewer in 'openapi' mode for $ref click-to-scroll. */
export function OpenApiSpecViewer() {
  const { t } = useTranslation();

  const [searchText, setSearchText] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(-1); // -1 = no selection
  const [searchMatchPath, setSearchMatchPath] = useState<(string | number)[] | null>(null);
  const viewerContainerRef = useRef<HTMLDivElement>(null);

  const scrollToMatch = (index: number) => {
    if (!viewerContainerRef.current || !data) return;

    const result = getPathToNthMatch(data, searchText, index);
    if (result) {
      setSearchMatchPath(result.path);
    }

    // Delay scroll to allow nodes to expand first
    setTimeout(() => {
      if (!viewerContainerRef.current) return;

      const matches = viewerContainerRef.current.querySelectorAll('[data-search-match="true"]');
      if (matches.length === 0) return;

      const prevCurrent = viewerContainerRef.current.querySelector('.json-current-match');
      if (prevCurrent) prevCurrent.classList.remove('json-current-match');

      const targetMatch = matches[index];
      if (targetMatch) {
        targetMatch.scrollIntoView({ behavior: 'smooth', block: 'center' });
        targetMatch.classList.add('json-current-match');
      }
    }, 150);
  };

  const { data, isLoading, error } = useQuery(openApiSpecQueryOptions);

  const matchCount = useMemo(() => {
    if (!data || !searchText) return 0;
    return countSearchMatches(data, searchText);
  }, [data, searchText]);

  const handleSearchChange = (newSearch: string) => {
    setSearchText(newSearch);
    setCurrentMatchIndex(-1);
    setSearchMatchPath(null);
  };

  const handlePrevMatch = () => {
    const newIndex = currentMatchIndex <= 0 ? matchCount - 1 : currentMatchIndex - 1;
    setCurrentMatchIndex(newIndex);
    scrollToMatch(newIndex);
  };

  const handleNextMatch = () => {
    const newIndex = currentMatchIndex < 0 || currentMatchIndex >= matchCount - 1 ? 0 : currentMatchIndex + 1;
    setCurrentMatchIndex(newIndex);
    scrollToMatch(newIndex);
  };

  if (isLoading) return <Spinner />;

  if (error) {
    return (
      <div className="flex items-center justify-center p-12">
        <span className="text-destructive">
          {t('error:load_resource', { resource: t('c:docs.openapi_specification').toLowerCase() })}
        </span>
      </div>
    );
  }

  const isSearching = searchText.length > 0;

  return (
    <>
      {/* Mobile-only: actions above the sticky bar */}
      <JsonActions
        url={openApiUrl}
        data={data}
        filename="openapi.json"
        resourceName={t('c:docs.openapi_json')}
        className="mb-2 w-full *:flex-1! sm:hidden"
      />

      <div className="sticky top-0 z-10 -mt-2 mb-2 flex w-full items-center gap-2 bg-background/60 py-2 backdrop-blur-xs max-sm:flex-col">
        <InputGroup className="max-sm:w-full">
          <InputGroupAddon>
            <SearchSpinner value={searchText} isSearching={false} />
          </InputGroupAddon>
          <InputGroupInput
            type="text"
            placeholder={`${t('c:search')}...`}
            value={searchText}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          <AnimatePresence mode="wait">
            {isSearching ? (
              <motion.div
                key="search-nav"
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.15 }}
                className="flex items-center overflow-hidden"
              >
                <span className="whitespace-nowrap px-2 text-muted-foreground text-xs">
                  {matchCount > 0
                    ? currentMatchIndex >= 0
                      ? `${currentMatchIndex + 1}/${matchCount}`
                      : `${matchCount}`
                    : t('c:no_results')}
                </span>
                <button
                  type="button"
                  onClick={handlePrevMatch}
                  disabled={matchCount === 0}
                  className="rounded p-1 hover:bg-muted disabled:opacity-30"
                  title={t('c:previous')}
                >
                  <ChevronUpIcon className="icon-sm" />
                </button>
                <button
                  type="button"
                  onClick={handleNextMatch}
                  disabled={matchCount === 0}
                  className="mr-2 rounded p-1 hover:bg-muted disabled:opacity-30"
                  title={t('c:next')}
                >
                  <ChevronDownIcon className="icon-sm" />
                </button>
              </motion.div>
            ) : null}
          </AnimatePresence>
          <InputGroupAddon align="inline-end">
            <CircleXIcon
              className={cn('cursor-pointer opacity-70 hover:opacity-100', searchText.length ? 'visible' : 'invisible')}
              onClick={() => handleSearchChange('')}
            />
          </InputGroupAddon>
        </InputGroup>

        <Button
          variant="outline"
          className="gap-2 max-sm:hidden"
          aria-label={isExpanded ? 'Reset to default' : 'Expand all'}
          onClick={() => {
            if (isExpanded) {
              // Reset to default state by forcing re-mount
              setResetKey((k) => k + 1);
            }
            setIsExpanded(!isExpanded);
          }}
        >
          {isExpanded ? <ChevronsDownUpIcon /> : <ChevronsUpDownIcon />}
          <span>{isExpanded ? t('c:reset') : t('c:expand')}</span>
        </Button>

        {/* Desktop-only actions; the mobile copy sits above the sticky bar */}
        <JsonActions
          url={openApiUrl}
          data={data}
          filename="openapi.json"
          resourceName={t('c:docs.openapi_json')}
          className="max-sm:hidden"
        />
      </div>

      <div ref={viewerContainerRef} className="overflow-x-auto rounded-lg bg-muted/30 p-4">
        <JsonViewer
          key={resetKey}
          value={data}
          openapiMode="spec"
          rootName={false}
          displayDataTypes={false}
          enableClipboard
          indentWidth={2}
          searchText={searchText}
          showKeyQuotes={false}
          expandAll={isExpanded}
          currentMatchIndex={currentMatchIndex}
          searchMatchPath={searchMatchPath}
        />
      </div>
    </>
  );
}
